import { Component, OnInit, Input, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FundAccountService } from '@pg-app/client/client-dashboard/components/fund-account/service/fund-account.service';
import { ToastrService } from 'ngx-toastr';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';

export interface Country {
  code: string;
  name: string;
}

export interface Bank {
  id: string;
  displayName: string;
  logoUrl: string;
  fullLogoUrl: string;
}

export interface Paging {
  currentPageNo: number;
  noOfItemsPerPage: number;
  totalNoOfPages: number;
}

export interface Search {
  countryCode: any;
  bankName: any;
}

export interface BanksInfo {
  banks: Bank[];
  paging: Paging;
  search: Search;
}

@Component({
  selector: 'np-iot-banks',
  templateUrl: './iot-banks.component.html',
  styleUrls: ['./iot-banks.component.scss']
})
export class IOTBanksComponent implements OnInit {
  // Paging
  public totalItems: Number;
  public pageSize = 10;
  currentPage = 1;

  countries: Country[];
  banksInfo: BanksInfo;

  bankSelectedId = '';
  bankSelectedDisplayName = '';
  bankSelectedCountryCode = '';

  dataLoadedOk = false;
  isLoadingData = true;

  searchForm: FormGroup;

  get searchCountryCtrl(): FormControl {
    return this.searchForm.get('searchCountry') as FormControl;
  }

  get searchBankNameCtrl(): FormControl {
    return this.searchForm.get('searchBankName') as FormControl;
  }

  constructor(
    public dialogRef: MatDialogRef<IOTBanksComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private fundAccountService: FundAccountService,
    private fb: FormBuilder,
    private toasterService: ToastrService
  ) {}

  ngOnInit() {
    // Building the form
    this.searchForm = this.fb.group({
      searchCountry: '',
      searchBankName: ''
    });

    // Initialisation
    this.countries = [];

    // Loading the countries
    const queryString = 'tradeReference=' + this.data.tradeReferenceCleaned 
                        + '&payingCurrencyCode=' + this.data.payingCurrencyCode;
    this.fundAccountService.getIOTCountries(queryString).subscribe(
      x => {
        this.countries = x;

        // If countries are loaded successfully, load the banks info
        this.loadBanksInfo(true, 1);
      },
      err => {
        this.dataLoadedOk = false;
        this.isLoadingData = false;
      }
    );
  }

  private loadBanksInfo(isNewSearch: boolean, pageNo: number) {
    // Initialisation
    if (isNewSearch) {
      this.bankSelectedId = '';
      this.bankSelectedDisplayName = '';
    }

    this.isLoadingData = true;

    const country = this.searchCountryCtrl.value;
    const bankName = this.searchBankNameCtrl.value;
    let queryString = 'tradeReference=' + this.data.tradeReferenceCleaned + '&pageNo=' + pageNo;
    if (country !== '') {
      queryString += '&countryCode=' + country;
    }
    if (bankName !== '') {
      queryString += '&bankName=' + bankName;
    }
    queryString += '&payingCurrencyCode=' + this.data.payingCurrencyCode;

    this.fundAccountService.getIOTBanks(queryString).subscribe(
      res => {
        this.dataLoadedOk = true;
        this.isLoadingData = false;

        this.banksInfo = res;

        // Restore the search values on UI
        this.searchCountryCtrl.setValue(this.banksInfo.search.countryCode ? this.banksInfo.search.countryCode : '');
        this.searchBankNameCtrl.setValue(this.banksInfo.search.bankName ? this.banksInfo.search.bankName : '');

        // Saving the values for paging UI
        this.currentPage = this.banksInfo.paging.currentPageNo;
        this.totalItems = this.banksInfo.banks.length > 0 ? this.banksInfo.paging.totalNoOfPages * this.banksInfo.paging.noOfItemsPerPage : 0;
        this.pageSize = this.banksInfo.paging.noOfItemsPerPage;
      },
      err => {
        this.dataLoadedOk = false;
        this.isLoadingData = false;

        this.ResetBanksInfo();
      }
    );
  }

  private ResetBanksInfo() {
    this.banksInfo = {
      banks: [],
      paging: {
        noOfItemsPerPage: 0,
        currentPageNo: 0,
        totalNoOfPages: 0
      },
      search: {
        countryCode: '',
        bankName: ''
      }
    };
  }

  bankSelected(bankId: any, bankDisplayName: any, bankCountryCode: any) {
    this.bankSelectedId = bankId;
    this.bankSelectedDisplayName = bankDisplayName;
    this.bankSelectedCountryCode = bankCountryCode;
  }

  buttonClicked(buttonId: any) {
    if (buttonId === '-1') {
      // Back
      this.dialogRef.close();
    } else if (buttonId === '0') {
      // Retry
      this.loadBanksInfo(true, 1);
    } else if (buttonId === '1') {
      // Next
      if (this.bankSelectedId) {
        this.dialogRef.close({bankId: this.bankSelectedId, bankName: this.bankSelectedDisplayName, bankCountryCode: this.bankSelectedCountryCode });
      } else {
        this.toasterService.error('Please select your online banking service');
      }
    } else if (buttonId === '2') {
      // Filtering
      this.loadBanksInfo(true, 1);
    }
  }

  onChangePage(event: any) {
    const pageNo = event;
    if (pageNo !== this.currentPage) {
      this.loadBanksInfo(false, pageNo);
    }
  }

  onSearchServiceProviderNameKeypress(event: any) {
    if (event.key === 'Enter') {
      this.loadBanksInfo(true, 1);
    }
  }
}
